import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TopBar from '../../components/TopBar';
import { Sparkline, AreaSpark, FunnelBars, /*GaugeArc*/ ScoreHistogram, RiskDonut, StackedBars } from '../../components/Charts';
import { useCrm, DEAL_STAGES, LEAD_STAGES, STAGE_META, isClosedStage } from '../../lib/crm';
import { isOpenDeal, isDealStage, toDeal, dealStageLabel, fmtMoneyEst } from '../../lib/deals';
import { useVehicleMap } from '../../lib/useVehicleMap';
import { useToast } from '../../lib/toast';
import { dashboardService } from '../../lib/services';
import { useCurrentUser } from '../../lib/currentUser';
import { ROLE_GROUPS } from '../../lib/roles';
import { downloadCsv, toCsv } from '../../lib/download';
import { useSnapshotCapture } from '../../components/dashboard/useSnapshotCapture';

/* ============================================================
   LEGACY dashboard — the single pre-role-split view.

   Kept verbatim (only the import paths moved) so the dealer-lens
   review sessions can compare old and new side by side, and as
   a fallback: /?view=legacy renders it. Delete after go-live
   sign-off on the three role dashboards.
   ============================================================ */

const PERIODS = [
  { id: 'today', label: 'Today', mult: 0.18 },
  { id: '7d', label: '7d', mult: 1.00 },
  { id: '30d', label: '30d', mult: 4.10 },
  { id: '90d', label: '90d', mult: 11.50 },
];

const PERIOD_DAYS = { today: 1, '7d': 7, '30d': 30, '90d': 90 };

// CRM funnel silhouette: fixed top/bottom widths (% of the card), stepped down
// evenly across the stages so the shape is always a funnel.
const FUNNEL_TOP_PCT = 100;
const FUNNEL_BOTTOM_PCT = 34;
const FUNNEL_ROW_H = 30;

// Which roles see the WHOLE dealership vs their OWN book.
// Sales manager, dealer principal, super admin and read-only auditor all
// look at everything; consultants and BDC agents see only their assigned
// leads/customers/deals. Service roles never land here (Dashboard.jsx
// redirects them to /service before this component ever renders).
/*
 * Derived from ROLE_GROUPS rather than listed by hand. This set predated the
 * General Manager and CEO roles, and because it was a private copy it silently
 * missed them — both landed here, were treated as consultants, and watched an
 * empty funnel while the Dealer Principal saw a full one. Deriving from the
 * shared groups means a role added there is dealership-wide here on the same
 * day, with no second list to remember.
 *
 * The auditor rides along explicitly: read-only, but an audit that sees only
 * part of the book is not an audit.
 */
const DEALERSHIP_WIDE_ROLES = new Set([...ROLE_GROUPS.MANAGEMENT, 'READ_ONLY_AUDITOR']);

export default function LegacyDashboard() {
  const navigate = useNavigate();
  // Snapshot: PNG of the dashboard, copied to the clipboard and downloaded.
  const { ref: snapshotRef, capture: exportSnapshot, snapping } = useSnapshotCapture();
  const { toast } = useToast();
  const { customers: crmCustomers, leads: crmLeads } = useCrm();
  const { vehiclesById } = useVehicleMap();
  const { role, consultantId } = useCurrentUser();

  // The CRM context also loads Agent Test Console records, which the Customer 360
  // and Lead Accelerator screens show but the dashboard's server-side counts
  // (`simulated = FALSE`) do not. Drop them here so the tiles, the funnel and the
  // customer totals reconcile with the server's numbers.
  const allCustomers = useMemo(() => crmCustomers.filter((c) => !c.simulated), [crmCustomers]);
  const allLeads = useMemo(() => crmLeads.filter((l) => !l.simulated), [crmLeads]);
  // Deals are leads at commercial stages; priced from the linked vehicle's
  // driveaway price where there is one (the context's own deals are un-priced,
  // which made every deal read as the default estimate).
  const allDeals = useMemo(
    () => allLeads
      .filter((l) => isDealStage(l.stage))
      .map((l) => toDeal(
        l,
        allCustomers.find((c) => c.id === l.customerId),
        l.vehicleOfInterestId ? vehiclesById.get(l.vehicleOfInterestId) : null,
      )),
    [allLeads, allCustomers, vehiclesById],
  );

  // Role-based scope. A consultant sees only what's assigned to them:
  //   • leads  → assignedConsultantId === my id
  //   • deals  → same rule (deals are leads at commercial stages)
  //   • customers → the ones behind those leads (so hot leads /
  //     at-risk / risk-split all read as "my book" instead of the
  //     dealership's).
  // Managers/dealer principals fall through unchanged — same numbers
  // they've always seen.
  const dealershipWide = DEALERSHIP_WIDE_ROLES.has(role);
  const myId = consultantId ? String(consultantId) : null;

  const leads = useMemo(() => {
    if (dealershipWide || !myId) return allLeads;
    return allLeads.filter((l) => String(l.assignedConsultantId ?? '') === myId);
  }, [allLeads, dealershipWide, myId]);

  const deals = useMemo(() => {
    if (dealershipWide || !myId) return allDeals;
    return allDeals.filter(
      (d) => String(d.assignedConsultantId ?? d.consultantId ?? '') === myId,
    );
  }, [allDeals, dealershipWide, myId]);

  const customers = useMemo(() => {
    if (dealershipWide || !myId) return allCustomers;
    const myCustomerIds = new Set(leads.map((l) => String(l.customerId)));
    return allCustomers.filter((c) => myCustomerIds.has(String(c.id)));
  }, [allCustomers, leads, dealershipWide, myId]);

  const [period, setPeriod] = useState('7d');
  const [snap, setSnap] = useState(null);
  const [snapErr, setSnapErr] = useState(null);
  const [series, setSeries] = useState({ leads: [], bookings: [], voice: [] });

  /* Live snapshot — refreshed when the period changes.
     Consultants ask the server for just their numbers by passing
     consultantId; the backend either honours the scope or falls back
     to dealership-wide (harmless — client-side filters still apply
     to derived KPIs below). */
  useEffect(() => {
    let cancelled = false;
    const days = PERIOD_DAYS[period] || 7;
    const params = { days };
    if (!dealershipWide && myId) params.consultantId = myId;
    dashboardService.snapshot(params)
      .then((s) => { if (!cancelled) { setSnap(s); setSnapErr(null); } })
      .catch((e) => { if (!cancelled) { setSnapErr(e); setSnap(null); } });
    return () => { cancelled = true; };
  }, [period, dealershipWide, myId]);

  /* Daily series for sparklines — refreshes alongside the snapshot. */
  useEffect(() => {
    let cancelled = false;
    const days = Math.max(7, PERIOD_DAYS[period] || 7);
    const seriesParams = (metric) => {
      const p = { metric, days };
      if (!dealershipWide && myId) p.consultantId = myId;
      return p;
    };
    Promise.all([
      dashboardService.series(seriesParams('leads')).catch(() => []),
      dashboardService.series(seriesParams('bookings')).catch(() => []),
      dashboardService.series(seriesParams('voice')).catch(() => []),
    ]).then(([leads, bookings, voice]) => {
      if (cancelled) return;
      const toValues = (arr) => Array.isArray(arr) ? arr.map(p => Number(p.value) || 0) : [];
      setSeries({
        leads: toValues(leads),
        bookings: toValues(bookings),
        voice: toValues(voice),
      });
    });
    return () => { cancelled = true; };
  }, [period, dealershipWide, myId]);

  const hotLeads = useMemo(() => leads.filter(l => l.score >= 80).sort((a, b) => b.score - a.score).slice(0, 5), [leads]);

  // CRM funnel — CUMULATIVE live counts per pipeline stage: each stage counts
  // the leads that have reached it OR GONE BEYOND it (current stage at or after
  // this one). So with 100 leads, 20 of them at Contacted or later, Inbound
  // reads 100 and Contacted reads 20 — a lead moving on no longer drains the
  // stage it came from. LOST leads are excluded (we only know where they ended,
  // not how far they got). Always in the canonical pipeline order — never
  // re-sorted by size.
  const funnelStages = useMemo(() => {
    const openStages = LEAD_STAGES.filter(s => s !== 'LOST');
    const rank = (stage) => openStages.indexOf(stage);   // -1 for LOST / unknown
    const counts = openStages.map((stage, i) => ({
      stage,
      label: STAGE_META[stage]?.label || stage,
      color: STAGE_META[stage]?.color || 'var(--text-tertiary)',
      count: leads.filter(l => rank(l.stage) >= i).length,
    }));

    const top = Math.max(1, ...counts.map(c => c.count));


    const withConversion = counts.map((c, i) => ({
      ...c,
      pctOfTop: c.count / top,
      convFromPrev: i === 0 || !counts[i - 1].count ? null
        : Math.round((c.count / counts[i - 1].count) * 100),
    }));


    return withConversion;
  }, [leads]);

  // Funnel geometry is FIXED, not data-driven: every stage steps down by the
  // same amount from FUNNEL_TOP_PCT to FUNNEL_BOTTOM_PCT, so the shape always
  // reads as a funnel regardless of the counts. The numbers are printed on the
  // band instead of being encoded in its width.
  const funnelTaper = useMemo(() => {
    const n = funnelStages.length;
    const step = n > 1 ? (FUNNEL_TOP_PCT - FUNNEL_BOTTOM_PCT) / n : 0;
    return funnelStages.map((s, i) => ({
      ...s,
      width: FUNNEL_TOP_PCT - step * i,
      nextWidth: FUNNEL_TOP_PCT - step * (i + 1),
    }));
  }, [funnelStages]);

  const atRiskCusts = useMemo(() => customers.filter(c => c.churnRisk === 'HIGH'), [customers]);

  // "Stalled" isn't a stage — it's a condition: an open lead with no activity
  // for 3+ days. (It used to test l.stage === 'STALLED', a UI-only stage that
  // no longer exists, so this tile always read zero.)
  const stalledLeads = useMemo(() => {
    const cutoff = Date.now() - 3 * 24 * 60 * 60 * 1000;
    return leads.filter((l) => {
      if (isClosedStage(l.stage)) return false;
      const last = l.lastActivityAt || l.createdAt;
      return last && new Date(last).getTime() < cutoff;
    }).length;
  }, [leads]);

  const openDealValue = useMemo(
    () => deals.filter(d => isOpenDeal(d.stage)).reduce((s, d) => s + d.value, 0),
    [deals],
  );
  const wonValue = useMemo(
    () => deals.filter(d => d.stage === 'DELIVERED').reduce((s, d) => s + d.value, 0),
    [deals],
  );
  // Deals with no driveaway price are valued at the default transaction price;
  // flag the totals so they are not read as confirmed revenue.
  const openDealEstimated = useMemo(
    () => deals.some(d => isOpenDeal(d.stage) && d.valueIsEstimated),
    [deals],
  );
  const wonDealEstimated = useMemo(
    () => deals.some(d => d.stage === 'DELIVERED' && d.valueIsEstimated),
    [deals],
  );

  /* ── Risk segmentation for the Customers tile ── */
  const riskSplit = useMemo(() => {
    const low = customers.filter(c => c.churnRisk === 'LOW').length;
    const med = customers.filter(c => c.churnRisk === 'MED').length;
    const high = customers.filter(c => c.churnRisk === 'HIGH').length;
    return [
      { l: 'LOW', v: low, c: 'var(--success)' },
      { l: 'MED', v: med, c: 'var(--warning)' },
      { l: 'HIGH', v: high, c: 'var(--danger)' },
    ];
  }, [customers]);

  /* ── Stage counts for the Deals tile ── */
  const dealStageBreakdown = useMemo(() => DEAL_STAGES.map(stage => {
    const matches = deals.filter(d => d.stage === stage);
    return {
      l: dealStageLabel(stage).slice(0, 4),
      open: isOpenDeal(stage) ? matches.length : 0,
      won: stage === 'DELIVERED' ? matches.length : 0,
    };
  }), [deals]);

  const periodDef = PERIODS.find(p => p.id === period) || PERIODS[1];
  const windowDays = PERIOD_DAYS[period] || 7;   // keep deep-links in step with the tile's window
  const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n}`;

  // Real counts come from /v1/dashboard/snapshot. Sparklines are still
  // synthetic until we ship a time-bucketed endpoint (Sprint 7).
  const snapCounts = {
    newLeads: snap?.newLeadsInWindow ?? 0,
    // Leads currently in the Booked stage (all time).
    bookedLeads: snap?.leadsByStage?.DELIVERY_BOOKED ?? 0,
    // Leads that moved INTO Booked during the selected period — what the
    // Bookings tile shows. Same set /leads?filter=booked&days=N lists.
    bookedInWindow: snap?.bookedInWindow ?? 0,
    bookings: snap?.bookingsInWindow ?? 0,
    hot: snap?.hotLeads ?? 0,
    stalled: snap?.stalledLeads ?? stalledLeads,
    voiceCalls: snap?.voiceCalls ?? 0,
    flagged: snap?.flaggedCalls ?? 0,
    avgSent: snap?.avgVoiceSentiment ?? 0,
    targets: snap?.totalCampaignTargets ?? 0,
    customers: snap?.totalCustomers ?? customers.length,
    leadsTotal: snap?.totalLeads ?? leads.length,
    bookingsTot: snap?.totalBookings ?? 0,
  };
  // Sparkline data is now derived from /v1/dashboard/series for the three
  // metrics we expose; the other tiles still show a static ramp until we
  // ship "lead-score history" + "at-risk customers per day" series.
  const sparkLeads = series.leads.length > 1 ? series.leads : [0];
  const sparkBookings = series.bookings.length > 1 ? series.bookings : [0];
  const sparkVoice = series.voice.length > 1 ? series.voice : [0];
  const sparkFallback = [2, 3, 4, 5, 6, 5, 7, 8, 9, 8, 10, 11];

  // `windowed` = the metric follows the date range. The others are point-in-time
  // ("now"): the whole live pipeline / all hot leads / all at-risk today — these
  // are labelled "now" so the range chip never implies a scope they don't honour.
  // A failed snapshot must not read as a genuine zero: show "—" and say so.
  const snapFailed = !!snapErr && !snap;
  const snapV = (n) => (snapFailed ? '—' : fmt(n));
  const UNAVAILABLE = 'data unavailable — could not load';
  const kpis = [
    { l: 'NEW LEADS', windowed: true, v: snapV(snapCounts.newLeads), sub: snapFailed ? UNAVAILABLE : `${snapCounts.leadsTotal} lifetime · new in ${periodDef.label}`, spark: sparkLeads, color: 'var(--accent-primary)', to: `/leads?filter=new&days=${windowDays}`, tooltip: 'Leads created in the selected window — click to see them in Lead Accelerator' },
    { l: 'HOT LEADS', windowed: false, v: snapV(snapCounts.hot), sub: snapFailed ? UNAVAILABLE : `${snapCounts.stalled} stalled`, subTo: '/leads?filter=stalled', spark: sparkFallback, color: 'var(--success)', good: true, to: '/leads?filter=hot', tooltip: 'Lead score ≥ 80 right now (not date-ranged) — click to see them in Lead Accelerator' },
    { l: 'BOOKINGS', windowed: true, v: snapV(snapCounts.bookedInWindow), sub: snapFailed ? UNAVAILABLE : `${snapCounts.bookedInWindow === 0 ? 'none' : snapCounts.bookedInWindow} moved to Booked in ${periodDef.label} · ${snapCounts.bookedLeads} in Booked now`, spark: sparkBookings, color: 'var(--accent-primary)', to: `/leads?filter=booked&days=${windowDays}`, tooltip: 'Bookings = leads that moved into the Booked stage (paperwork done, handover scheduled) in the selected window. Not appointments, test drives or the cumulative funnel count — click to see them in Lead Accelerator' },
    { l: 'VOICE QA', windowed: true, v: snapV(snapCounts.voiceCalls), sub: snapFailed ? UNAVAILABLE : (snapCounts.voiceCalls === 0 ? `no voice calls captured in ${periodDef.label}` : `${snapCounts.flagged} flagged · avg sent ${snapCounts.avgSent.toFixed(2)}`), spark: sparkVoice, color: snapCounts.flagged > 0 ? 'var(--warning)' : 'var(--success)', warn: snapCounts.flagged > 0, to: '/triage', tooltip: 'Voice calls captured + flagged by the triage analyser, in the selected window' },
    { l: 'AT-RISK CUSTOMERS', windowed: false, v: `${atRiskCusts.length}`, sub: `${snapCounts.stalled} stalled leads`, subTo: '/leads?filter=stalled', spark: [2, 3, 2, 4, 3, 5, 4, 3, 5, 4], color: 'var(--danger)', warn: true, to: '/customers?risk=HIGH', tooltip: 'At-risk customer = a customer with no contact in 60+ days (click the card to see them). Stalled lead = an open lead with no activity for 3+ days (click the red line to see them). Both as of today, not date-ranged.' },
  ];

  // Real CSV export of the leads currently in scope (dealership-wide for
  // managers, own book for consultants). Built + downloaded client-side.
  const exportLeadsCsv = () => {
    if (!leads.length) { toast('No leads to export', { variant: 'info' }); return; }
    const nameById = new Map(customers.map((c) => [String(c.id), c.name]));
    const rows = leads.map((l) => [
      nameById.get(String(l.customerId)) || l.customerName || l.customerId || '',
      STAGE_META[l.stage]?.label || l.stage || '',
      l.score ?? '',
      l.source || '',
      l.createdAt ? new Date(l.createdAt).toISOString().slice(0, 10) : '',
    ]);
    downloadCsv(
      `leads-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(['Customer', 'Stage', 'Score', 'Source', 'Created'], rows),
    );
    toast(`Exported ${leads.length} leads to CSV`, { variant: 'success' });
  };

  const quickActions = [
    // Each action opens its real target. New customer / New lead deep-link into
    // the respective page with ?new=1, which auto-opens that page's create dialog.
    { icon: 'C+', label: 'New customer', onClick: () => navigate('/customers?new=1') },
    { icon: 'D+', label: 'New deal', onClick: () => navigate('/quotes/new') },
    { icon: 'L+', label: 'New lead', onClick: () => navigate('/leads?new=1') },
    { icon: '📣', label: 'Send broadcast', onClick: () => navigate('/campaigns') },
    { icon: 'AU', label: 'Audit pack', onClick: () => navigate('/compliance') },
    { icon: 'EX', label: 'Export CSV', onClick: exportLeadsCsv },
  ];

  /* ── Module shortcuts — live counts from the snapshot ── */
  const targetsByState = snap?.campaignTargetsByState || {};
  // const bookingsByStatus = snap?.bookingsByStatus || {};

  const moduleTiles = [
    {
      t: 'LEAD ACCELERATOR',
      m: `${snapCounts.leadsTotal} active · ${snapCounts.hot} hot · ${snapCounts.stalled} stalled`,
      to: '/leads',
      accent: 'var(--accent-primary)',
      chart: <AreaSpark values={sparkLeads.length > 1 ? sparkLeads : sparkFallback.slice(0, 10)} w={220} h={48} color="var(--accent-primary)" />,
      caption: `${snapCounts.newLeads} new · ${periodDef.label}`,
    },
    {
      t: 'LOST LEADS',
      m: snapFailed
        ? UNAVAILABLE
        : (snapCounts.targets === 0
          ? 'no revival targets yet — they appear once a batch of leads idle 30+ days is built'
          : `${snapCounts.targets} targets · ${targetsByState.CONTACTED || 0} contacted`),
      to: '/dead-leads',
      accent: 'var(--warning)',
      chart: (
        <FunnelBars w={220} h={48} segments={[
          { l: 'POOL', v: snapCounts.targets, c: 'var(--text-tertiary)' },
          { l: 'CONTACTED', v: targetsByState.CONTACTED || 0, c: 'var(--info)' },
          { l: 'REPLIED', v: targetsByState.REPLIED || 0, c: 'var(--warning)' },
          { l: 'CONVERTED', v: targetsByState.CONVERTED || 0, c: 'var(--success)' },
        ]} />
      ),
      caption: `Revival funnel · ${periodDef.label}`,
    },
    // {
    //   t: 'SERVICE ADVISOR',
    //   m: (() => {
    //     const confirmed = bookingsByStatus.CONFIRMED || 0;
    //     const total = snapCounts.bookingsTot || 1;
    //     return `${snapCounts.bookingsTot} bookings · ${confirmed} confirmed`;
    //   })(),
    //   to: '/service',
    //   accent: 'var(--success)',
    //   chart: <GaugeArc
    //     value={Math.min(1, (bookingsByStatus.CONFIRMED || 0) / Math.max(1, snapCounts.bookingsTot))}
    //     w={220} h={60}
    //     color="var(--success)"
    //     label="CONFIRMED %"
    //   />,
    //   caption: 'Confirmed vs total bookings',
    // },
    {
      t: 'TRIAGE & QA',
      m: `${snapCounts.voiceCalls} voice calls · ${snapCounts.flagged} flagged`,
      to: '/triage',
      accent: 'var(--warning)',
      chart: <ScoreHistogram w={220} h={48} buckets={[2, 3, 5, 8, 11, 14, 18, 24, 30, 38, 52, 68, 84, 102, 118, 130, 98, 52]} />,
      caption: `Avg sentiment ${snapCounts.avgSent.toFixed(2)}`,
    },
    {
      t: 'CUSTOMERS',
      m: `${snapCounts.customers} in CRM · ${atRiskCusts.length} at risk · ${snapCounts.hot} hot`,
      to: '/customers',
      accent: 'var(--info)',
      chart: <RiskDonut w={220} h={60} segments={riskSplit} />,
      caption: 'Risk segmentation',
    },
    {
      t: 'DEALS',
      m: `${fmtMoneyEst(openDealValue, openDealEstimated)} open · ${fmtMoneyEst(wonValue, wonDealEstimated)} won${(openDealEstimated || wonDealEstimated) ? ' (~ estimate)' : ''}`,
      to: '/deals',
      accent: 'var(--accent-primary)',
      chart: <StackedBars w={220} h={48} groups={dealStageBreakdown} />,
      caption: 'Open vs won · by stage',
    },
  ];

  return (
    <>
      <TopBar
        crumbs={['Home', dealershipWide ? 'Dealership' : 'My book']}
        right={
          <>
            <div className="period-toggle" role="tablist" aria-label="Period">
              {PERIODS.map(p => (
                <button
                  key={p.id}
                  role="tab"
                  aria-selected={period === p.id}
                  onClick={() => setPeriod(p.id)}
                  className={period === p.id ? 'active' : ''}
                >{p.label}</button>
              ))}
            </div>
            <button className="btn small" onClick={exportSnapshot} disabled={snapping}
              title="Capture the dashboard as an image — copied to your clipboard (paste into a deck/email/chat) and downloaded as a PNG">
              {snapping ? 'Capturing…' : '📸 Snapshot'}
            </button>
          </>
        }
      />
      <div className="app-content" ref={snapshotRef}>
        {/* ── KPI row ── */}
        <div className="grid-5" style={{ marginBottom: 16 }}>
          {kpis.map((k, i) => (
            <button
              key={i}
              className="kpi-tile animate-in"
              style={{ animationDelay: `${i * 0.05}s`, '--kpi-accent': k.color }}
              onClick={() => k.to && navigate(k.to)}
              title={k.tooltip}
            >
              <div className="kpi-head">
                <span className="card-label">{k.l}</span>
                <span className="kpi-period mono text-xs text-muted"
                  title={k.windowed ? 'Follows the selected date range' : 'Current state — not affected by the date range'}>
                  {k.windowed ? periodDef.label : 'now'}
                </span>
              </div>
              <div className="metric-value" style={{ color: k.color }}>{k.v}</div>
              <div className={`metric-sub ${k.warn ? 'danger' : k.good ? 'success' : ''}`}>
                {k.subTo ? (
                  // The sub-line is its own link (stalled leads), separate from the card's click target.
                  <span
                    role="link"
                    tabIndex={0}
                    title="Stalled leads: open leads with no activity for 3+ days — click to see them"
                    style={{ textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); navigate(k.subTo); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate(k.subTo); } }}
                  >{k.sub}</span>
                ) : k.sub}
              </div>
              <div className="kpi-spark">
                <Sparkline values={k.spark} w={180} h={32} color={k.color} />
              </div>
              <div className="kpi-tooltip">{k.tooltip}</div>
            </button>
          ))}
        </div>

        {/* ── Quick actions ── */}
        <div className="card" style={{ marginBottom: 14, padding: '12px 14px' }}>
          <div className="flex-between" style={{ marginBottom: 8 }}>
            <span className="card-label" style={{ margin: 0 }}>QUICK ACTIONS</span>
            <span className="mono text-xs text-muted">click to perform</span>
          </div>
          <div className="quick-actions">
            {quickActions.map((a, i) => (
              <button key={i} className="quick-action" onClick={a.onClick}>
                <span className="qa-icon mono">{a.icon}</span>
                <span className="qa-label">{a.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── CRM funnel + widgets ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div className="card animate-in" style={{ animationDelay: '0.2s', position: 'relative' }}>
            <div className="flex-between" style={{ marginBottom: 12 }}>
              <span className="card-label" style={{ margin: 0 }}>
                CRM FUNNEL <span className="mono text-xs text-muted" style={{ fontWeight: 400 }}
                  title="Leads that have reached each stage or gone beyond it. The current live pipeline — not scoped to the date range">· now</span>
              </span>
              <Link to="/leads" className="mono text-xs" style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>open pipeline</Link>
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: -6, marginBottom: 8 }}>
              Leads that reached each stage or beyond
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {funnelStages.every(s => s.count === 0) ? (
                <div className="empty-state" style={{ padding: '24px 8px' }}>
                  <div className="title">No leads in the pipeline yet</div>
                </div>
              ) : (
                funnelTaper.map((s) => {
                  const topPct = s.width;
                  const botPct = s.nextWidth;
                  const isEmpty = s.count <= 0;

                  return (
                    <Link
                      key={s.stage}
                      to={`/leads?stage=${encodeURIComponent(s.stage)}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}
                      title={`${s.label} · ${s.count} lead${s.count === 1 ? '' : 's'} reached this stage or beyond — click to open this stage`}
                    >
                      <span className="mono text-xs" style={{ width: 78, flexShrink: 0, color: 'var(--text-secondary)', textAlign: 'right' }}>
                        {s.label}
                      </span>
                      <div style={{ flex: 1, height: FUNNEL_ROW_H, position: 'relative' }}>
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: s.color,
                          opacity: isEmpty ? 0.30 : 1,
                          clipPath: `polygon(${50 - topPct / 2}% 0, ${50 + topPct / 2}% 0, ${50 + botPct / 2}% 100%, ${50 - botPct / 2}% 100%)`,
                        }} />
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'linear-gradient(90deg, transparent 8%, rgba(0,0,0,0.18) 28%, rgba(0,0,0,0.18) 72%, transparent 92%)',
                          clipPath: `polygon(${50 - topPct / 2}% 0, ${50 + topPct / 2}% 0, ${50 + botPct / 2}% 100%, ${50 - botPct / 2}% 100%)`,
                          pointerEvents: 'none',
                        }} />
                        <span
                          className="mono"
                          style={{
                            position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
                            color: '#fff',
                            textShadow: '0 1px 3px rgba(0,0,0,0.55), 0 0 8px rgba(0,0,0,0.20)',
                            opacity: isEmpty ? 0.60 : 1,
                          }}
                        >
                          {s.count}
                        </span>
                      </div>
                      {/* Step-conversion % column hidden by request (all logins).
                      <span className="mono text-xs" style={{ width: 58, flexShrink: 0, color: 'var(--text-tertiary)', textAlign: 'left' }}>
                        {s.convFromPrev != null ? `${s.convFromPrev}%` : ''}
                      </span>
                      */}
                    </Link>
                  );
                })
              )}
            </div>
          </div>

          <div className="card animate-in" style={{ animationDelay: '0.25s' }}>
            <div className="flex-between" style={{ marginBottom: 10 }}>
              <span className="card-label" style={{ margin: 0 }}>
                HOT LEADS <span className="mono text-xs text-muted" style={{ fontWeight: 400 }}
                  title="Leads scoring ≥ 80 right now — not scoped to the date range">· now</span>
              </span>
              <Link to="/leads" className="mono text-xs" style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>see all</Link>
            </div>
            {hotLeads.length === 0 ? (
              <div className="empty-state" style={{ padding: '24px 8px' }}><div className="title">No hot leads</div></div>
            ) : (
              hotLeads.map(l => {
                const cust = customers.find(c => c.id === l.customerId);
                return (
                  <Link key={l.id} to={`/leads/${l.id}`} className="hot-lead-row">
                    <span className="avatar-pill" style={{ width: 28, height: 28, fontSize: 10 }}>{cust?.initials || '?'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{cust?.displayName || 'Unknown'}</div>
                      <div className="mono text-xs text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.vehicle}</div>
                    </div>
                    <div className="score-badge" style={{ background: `conic-gradient(var(--success) ${l.score * 3.6}deg, var(--border-color) 0)` }}>
                      <span>{l.score}</span>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          <div className="card animate-in" style={{ animationDelay: '0.3s' }}>
            <div className="flex-between" style={{ marginBottom: 10 }}>
              <span className="card-label" style={{ margin: 0 }}>
                AT-RISK CUSTOMERS <span className="mono text-xs text-muted" style={{ fontWeight: 400 }}
                  title="No contact in 60+ days as of today — not scoped to the date range">· now</span>
              </span>
              <Link to="/customers?risk=HIGH" className="mono text-xs" style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>see all</Link>
            </div>
            {atRiskCusts.length === 0 ? (
              <div className="empty-state" style={{ padding: '24px 8px' }}><div className="title">All customers healthy</div></div>
            ) : (
              atRiskCusts.slice(0, 5).map(c => (
                <Link key={c.id} to={`/customers/${c.id}`} className="hot-lead-row">
                  <span className="avatar-pill" style={{ width: 28, height: 28, fontSize: 10, background: 'var(--danger-bg)', color: 'var(--danger)' }}>
                    {c.initials}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.displayName || c.name}</div>
                    <div className="mono text-xs text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.churnReason}</div>
                  </div>
                  <span className="risk-pill risk-HIGH">HIGH</span>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* ── Module shortcuts — each with its OWN chart style ── */}
        <div className="module-grid">
          {moduleTiles.map((m, i) => (
            <Link
              key={i}
              to={m.to}
              className="card module-tile animate-in"
              style={{
                animationDelay: `${0.35 + i * 0.04}s`,
                textDecoration: 'none', color: 'inherit',
                borderTop: `2px solid ${m.accent}`,
              }}
            >
              <div className="flex-between" style={{ marginBottom: 6 }}>
                <span className="card-label" style={{ margin: 0 }}>{m.t}</span>
                <span className="module-arrow">→</span>
              </div>
              <div style={{ fontSize: 13, marginTop: 4, marginBottom: 10, color: 'var(--text-secondary)' }}>{m.m}</div>
              <div className="module-chart">{m.chart}</div>
              <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>{m.caption}</div>
            </Link>
          ))}
        </div>
      </div >
    </>
  );
}